import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { LetDirective } from '@directives/app-let.directive';
import { Store } from '@ngrx/store';
import { selectAppCmsCopy } from '@selectors/cms.selector';
import { NullableString, SelectItem } from '@models/app.models';
import { RequiredFieldPipe } from 'src/app/pipes/required-field/required-field.pipe';
import { stringAsBooleanProp } from 'src/app/utils/data-utils';
import { InputErrorsComponent } from '../input-errors/input-errors.component';
import { selectIsMobile, selectIsWeb } from '@selectors/app.selector';

@Component({
  selector: 'app-select',
  standalone: true,
  imports: [
    RequiredFieldPipe,
    ReactiveFormsModule,
    CommonModule,
    InputErrorsComponent,
    LetDirective,
  ],
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectComponent implements OnInit {
  // sorry mom
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() items: any[] | undefined | null;
  @Input() label: NullableString;
  @Input() name: string;
  @Input() customClass: string;

  // index is a special value that uses the values of each item
  // in the array rather than a key into each item.
  @Input() textKey: string = 'index';
  @Input() valueKey: string = 'index';
  @Input() extraErrors?: { [key: string]: string };
  @Input() suppressErrors?: string[];
  @Input() customInput = false;
  @Input() customInputValid = false;
  @Input() hideErrorLabel = false;
  @Input() readonly = false;
  @Input() warning = false;

  public _required = false;
  public isSelectOpen = false;
  @Input()
  get required(): string | boolean {
    return this._required;
  }
  set required(value: string | boolean) {
    this._required =
      typeof value === 'string' ? stringAsBooleanProp(value) : value;
  }

  public _disabled = false;
  @Input() get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = value;
    this._disabled ? this.control?.disable() : this.control?.enable();
  }
  @Input() group?: string | number;
  @Input() control?: AbstractControl | UntypedFormControl | null;
  @Input() value: string | number;
  @Input() defaultItem: SelectItem = { text: '', value: '' };
  @Input() showDefault: boolean = true;
  @Output() valueChange = new EventEmitter<string>();

  appCopy$ = this.store.select(selectAppCmsCopy);

  get formControl(): UntypedFormControl | null {
    if (!this.control) {
      return null;
    }
    return this.control as UntypedFormControl;
  }

  id: string;

  isMobile$ = this.store.select(selectIsMobile);
  isWeb$ = this.store.select(selectIsWeb);

  constructor(private store: Store) {}

  ngOnInit(): void {
    this.id =
      this.group !== undefined && this.name
        ? `${this.group}-${this.name}`
        : this.name;
  }

  onChange(event: Event): void {
    const selectedValue = (event.target as HTMLSelectElement).value;
    this.valueChange.emit(selectedValue);
  }
}
